import {useQuery} from "@tanstack/react-query";
import {AxiosError} from "axios";
import {TBooking} from "../../../redux/call/i-call";
import {PaginationMeta, TServiceApiResponse} from "../../../redux/service/i-service";
import {createPastCallsRepository} from "../data/repositories";
import {PaginatedPastCalls, PaginationParams, PastCall} from "../domain/types";

/**
 * Query key for coach past calls.
 * Kept identical to the legacy key (["past_calls", page]) so cache entries stay
 * shared across the old hook and the migrated slice during rollout.
 */
export const PAST_CALLS_QUERY_KEY = "past_calls";

/**
 * Domain hook: fetch the coach's past calls as clean PastCall domain objects.
 *
 * @param params - Pagination params (page, perPage)
 * @param enabled - Whether the query runs (default true)
 */
export const useCoachPastCalls = (params?: PaginationParams, enabled: boolean = true) => {
  const repository = createPastCallsRepository();

  return useQuery<PaginatedPastCalls, AxiosError<{status: boolean; message: string}>>({
    queryKey: [PAST_CALLS_QUERY_KEY, params?.page ?? 1, params?.perPage],
    queryFn: () => repository.getCoachPastCalls(params),
    enabled,
    keepPreviousData: true,
  });
};

/**
 * Builds a legacy `service` stub (Omit<TServiceApiResponse, "statusName">) from a
 * PastCall so the existing past-call card/modal keep rendering unchanged.
 */
function toLegacyServiceStub(call: PastCall): Omit<TServiceApiResponse, "statusName"> {
  const isBundle = call.bundleSession != null;

  return {
    id: 0,
    userId: 0,
    name: call.serviceName,
    slug: "",
    description: call.serviceDescription,
    callDuration: call.duration,
    callPrice: call.amount,
    callPriceCurrency: call.currency,
    currencies: [],
    audioLocation: call.bookingType === "audio",
    videoLocation: call.bookingType === "video",
    bookingQuestions: null,
    booking_interval: null,
    cover_picture: null,
    createdAt: "",
    updatedAt: "",
    tiny_url: "",
    hideServiceFromMenu: false,
    request_phone_number: false,
    url_redirect: null,
    status_id: 1,
    is_bundle: isBundle,
    // The legacy service contract types this as a string.
    no_of_sessions:
      isBundle && call.bundleSessionCount != null
        ? String(call.bundleSessionCount)
        : null,
    enable_currency_conversion: false,
    revenue: [],
  };
}

/**
 * Converts a PastCall domain object into the legacy TBooking shape consumed by
 * the coach "Past sessions" UI (PastCallCard, PastCallDetailsModal).
 *
 * This is a strangler bridge: it lets the migration ship without touching the
 * presentation layer. Remove it once those components read the domain type.
 */
function toLegacyBooking(call: PastCall): TBooking {
  return {
    id: call.id,
    reference: call.reference,
    amount: call.amount,
    currency: call.currency,
    booking_year: call.bookingYear,
    booking_month: call.bookingMonth,
    booking_date: call.bookingDate,
    booking_type: call.bookingType,
    coach_from_time: call.coachFromTime,
    coach_to_time: call.coachToTime,
    timeslot: call.timeslot,
    first_name: call.firstName,
    last_name: call.lastName,
    email_address: call.emailAddress,
    phone_number: call.phoneNumber,
    // v2 past-calls payload carries no separate coach identity or notes.
    // The UI shows the customer name from first_name/last_name directly.
    coach_first_name: "",
    coach_last_name: "",
    coach_display_name: null,
    coach_username: "",
    display_name: null,
    notes: "",
    bookingQuestions: call.bookingQuestions,
    cancellationReason: null,
    createdAt: "",
    updatedAt: "",
    endTime: "",
    mentor_timezone: "",
    user_timezone: "",
    is_paid: call.amount > 0,
    is_bundle: call.bundleSession != null,
    bundle_reference: null,
    bundle_session: call.bundleSession == null ? null : String(call.bundleSession),
    roomId: call.roomId,
    video_platform: (call.videoPlatform as TBooking["video_platform"]) ?? "videoSdk",
    service: toLegacyServiceStub(call),
    serviceId: 0,
    statusId: 1,
    statusName: "Active",
    userId: 0,
  };
}

/**
 * Builds legacy PaginationMeta from the domain pagination flags.
 * CustomPagination only consumes next_page/previous_page for its buttons.
 */
function toLegacyMeta(
  result: PaginatedPastCalls,
  page: number,
  perPage: number
): PaginationMeta {
  return {
    current_page: page,
    per_page: perPage,
    total_pages: result.hasNextPage ? page + 1 : page,
    next_page: result.hasNextPage ? page + 1 : 0,
    previous_page: result.hasPrevPage ? page - 1 : 0,
  };
}

/**
 * Legacy-compatible hook for the coach "Past sessions" tab.
 *
 * Returns the historical `{ data: { bookings: TBooking[] }, meta }` shape so the
 * existing UI components require no changes. New code should prefer
 * {@link useCoachPastCalls}.
 */
export const useCoachPastCallsLegacy = (page: number = 1, perPage: number = 5) => {
  const repository = createPastCallsRepository();

  return useQuery<
    {data: {bookings: TBooking[]}; meta: PaginationMeta},
    AxiosError<{status: boolean; message: string}>
  >({
    queryKey: [PAST_CALLS_QUERY_KEY, page, perPage],
    queryFn: async () => {
      const result = await repository.getCoachPastCalls({page, perPage});
      return {
        data: {bookings: result.data.map(toLegacyBooking)},
        meta: toLegacyMeta(result, page, perPage),
      };
    },
    keepPreviousData: true,
  });
};
